import React from 'react';
import { useTranslation } from 'react-i18next';
import './MetricWidget.css';

const MetricWidget = ({ data, config }) => {
    const { t } = useTranslation();
    // Handle missing or invalid data - default to 0
    const value = data?.value ?? 0;
    // `null` EXPLICITE = la mesure n'a pas de sens ici, à distinguer d'un vrai zéro. Cas type :
    // un taux dont le dénominateur est vide (aucun rappel mesurable). Afficher « 0 % » se lirait
    // « aucune promesse tenue » alors qu'aucune n'a été mesurée — l'inverse de la vérité.
    // `undefined` reste traité comme 0 : c'est le cas « pas encore de données », et le changer
    // toucherait les 42 autres cartes du catalogue.
    const notApplicable = data?.value === null;
    const { trend, percentage, targetValue, maxValue } = data || {};

    // Round any decimal to max 1 digit. Integers stay clean.
    const round1 = (n) => Number.isInteger(n) ? n : Math.round(n * 10) / 10;

    // Format value based on config.format. All decimals capped at 1 digit.
    const formatValue = (val) => {
        const n = Number(val);
        if (!isFinite(n)) return '-';
        switch (config?.format) {
            case 'duration': {
                if (n < 0) return '-';
                // Garde-fou : valeur > 24h pour une durée d'appel = donnée corrompue
                if (n > 86400) return '—';
                const minutes = Math.floor(n / 60);
                const seconds = Math.floor(n % 60);
                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
            }
            // Durée SANS plafond de 24 h, en `hh:mm:ss`. Le canal mail en a besoin : un dossier
            // vit des jours (l'auto-clôture d'un flux est à 7 jours), et le garde-fou ci-dessus
            // — écrit pour des durées d'APPEL, où 24 h signale une donnée corrompue — rendrait
            // « — » sur un délai de résolution parfaitement normal.
            // Même sortie que `formatDuration` de Utils/excelExport.js : l'écran et le fichier
            // exporté doivent afficher la même chose, au caractère près.
            case 'longDuration': {
                if (n < 0) return '-';
                const total = Math.floor(n);
                const pad = (v) => String(v).padStart(2, '0');
                return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
            }
            case 'percentage':
                return `${round1(n)}%`;
            case 'number':
            default:
                return round1(n).toLocaleString();
        }
    };

    // Determine color class based on current value vs target and max thresholds
    // GREEN: value < target (goal achieved)
    // ORANGE: target ≤ value < max (warning zone)
    // RED: value ≥ max (exceeded limit)
    let colorClass = '';
    if (targetValue !== undefined && targetValue !== null && maxValue !== undefined && maxValue !== null && maxValue > 0) {
        // Use min/max so the logic works regardless of which threshold is numerically larger
        const lowerThreshold = Math.min(targetValue, maxValue);
        const upperThreshold = Math.max(targetValue, maxValue);

        if (value < lowerThreshold) {
            colorClass = 'value-green';
        } else if (value < upperThreshold) {
            colorClass = 'value-orange';
        } else {
            colorClass = 'value-red';
        }
    }

    return (
        <div className="metric-widget">
            <div className={`metric-value ${colorClass}`}>
                {notApplicable ? '—' : formatValue(value)}
            </div>

            {/* Display target and max if available */}
            {targetValue !== undefined && targetValue !== null && (
                <div className="metric-thresholds">
                    <div className="metric-target">
                        <span className="threshold-label">{t('TARGET')}:</span>
                        <span className="threshold-value">{formatValue(targetValue)}</span>
                    </div>
                    {maxValue !== undefined && maxValue !== null && (
                        <div className="metric-max">
                            <span className="threshold-label">{t('MAX')}:</span>
                            <span className="threshold-value">{formatValue(maxValue)}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Display percentage if available and no target */}
            {!targetValue && percentage !== undefined && percentage !== null && (
                <div className="metric-trend">{percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1)}%</div>
            )}
        </div>
    );
};

export default MetricWidget;
